import { CloudUpload, Users, Send, Settings, SlidersHorizontal } from 'lucide-react';

export type AdminSection = 'ingestion' | 'candidates' | 'batches' | 'access' | 'configurator';

export const SIDEBAR_ITEMS: { id: AdminSection; label: string; icon: React.ElementType }[] = [
  { id: 'ingestion', label: 'Data Ingestion', icon: CloudUpload },
  { id: 'candidates', label: 'Candidates', icon: Users },
  { id: 'batches', label: 'Batches', icon: Send },
  { id: 'access', label: 'Access Management', icon: Settings },
  { id: 'configurator', label: 'Profile Configurator', icon: SlidersHorizontal },
];

export const PAGE_META: Record<AdminSection, { title: string; description: string }> = {
  ingestion: { title: 'Data Ingestion', description: 'Upload candidate CSVs and review past enrichment runs.' },
  candidates: { title: 'Candidates', description: 'Manage all candidates in the system.' },
  batches: { title: 'Batches', description: 'Manage and send candidate batches to companies.' },
  access: { title: 'Access Management', description: 'Manage companies and their users who have access to the platform.' },
  configurator: { title: 'Profile Configurator', description: 'Customize the order and visibility of sections in the candidate profile view for each domain.' },
};

// Pipeline steps — IDs match what the backend emits in currentStep
export interface PipelineStep {
  id: string;
  label: string;
  description: string;
  hasAi: boolean;
}

export const PIPELINE_STEPS: PipelineStep[] = [
  {
    id: 'matching-fraud-check',
    label: 'Fraud Check (Matching API)',
    description: 'Primary fraud check via the Turing Matching service (resume + LinkedIn + web verification + aggregator). Runs first; the local fraud steps below run only when Matching is unavailable or does not cover a candidate.',
    hasAi: false,
  },
  {
    id: 'data-preparation',
    label: 'Data Preparation',
    description: 'Converts CSV rows into structured JSON profiles using an LLM with domain-specific prompts, then merges them with existing data into a unified index.',
    hasAi: true,
  },
  {
    id: 'linkedin-validation',
    label: 'LinkedIn Validation',
    description: 'Fetches LinkedIn profile data and checks whether identity, work history, and education are verified on LinkedIn.',
    hasAi: false,
  },
  {
    id: 'single-agent-local-resume-check',
    label: 'Single Agent : Local Resume Check',
    description: 'An LLM evaluates each resume for internal consistency, plausibility, and language signals. Scores 6 categories on a 0–3 scale and flags factually impossible claims. No external sources used.',
    hasAi: true,
  },
  {
    id: 'multi-agent-web-verification',
    label: 'Multi Agent : Web Verification',
    description: 'Three parallel agents with web-search capability independently verify education claims, work/employment history, and domain-specific credentials (GitHub for SWE, publications for STEM, certifications for Finance).',
    hasAi: true,
  },
  {
    id: 'final-consolidation',
    label: 'Final Consolidation',
    description: 'A synthesis agent produces a holistic assessment — final verdict (REAL / FAKE / UNCERTAIN), confidence level, justification bullets, and red flags. Then applies rule-based scoring to produce binary YES/NO decisions.',
    hasAi: true,
  },
];

export const STEP_ORDER = PIPELINE_STEPS.map((s) => s.id);

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

/**
 * @param skippedSteps step ids that were skipped this run (e.g. local fraud
 *   steps when the Matching API fully covered the batch). These always render as
 *   'skipped', overriding completed/failed, so the UI reflects that they did not run.
 */
export function deriveStepStatuses(
  currentStep: string | null,
  runStatus: string,
  skippedSteps: string[] = [],
): Record<string, StepStatus> {
  const result: Record<string, StepStatus> = {};
  PIPELINE_STEPS.forEach((s) => { result[s.id] = 'pending'; });
  const done = (): Record<string, StepStatus> => {
    skippedSteps.forEach((id) => { if (id in result) result[id] = 'skipped'; });
    return result;
  };

  if (runStatus === 'failed') {
    for (const step of PIPELINE_STEPS) {
      if (step.id === currentStep) { result[step.id] = 'failed'; break; }
      result[step.id] = 'completed';
    }
    return done();
  }

  if (runStatus === 'completed') {
    PIPELINE_STEPS.forEach((s) => { result[s.id] = 'completed'; });
    return done();
  }

  if (runStatus === 'queued' || !currentStep || currentStep === 'config') return done();
  if (currentStep === 'complete') {
    PIPELINE_STEPS.forEach((s) => { result[s.id] = 'completed'; });
    return done();
  }

  const STEP_ALIASES: Record<string, string> = { 'link-check': 'final-consolidation' };
  const resolvedStep = STEP_ALIASES[currentStep] ?? currentStep;
  const currentIdx = STEP_ORDER.indexOf(resolvedStep);
  if (currentIdx < 0) return done();

  for (let i = 0; i < STEP_ORDER.length; i++) {
    if (i < currentIdx) result[STEP_ORDER[i]] = 'completed';
    else if (i === currentIdx) result[STEP_ORDER[i]] = 'running';
  }

  return done();
}

export function getStepProgress(currentStep: string | null, status: string): number {
  if (status === 'queued') return 0;
  if (status === 'completed') return 100;
  const PROGRESS_ALIASES: Record<string, string> = { 'link-check': 'final-consolidation' };
  const resolved = PROGRESS_ALIASES[currentStep ?? ''] ?? (currentStep ?? '');
  if (status === 'failed') {
    const idx = STEP_ORDER.indexOf(resolved);
    return idx >= 0 ? Math.round(((idx + 0.5) / STEP_ORDER.length) * 100) : 0;
  }
  if (!currentStep || currentStep === 'config') return 0;
  if (currentStep === 'complete') return 100;
  const idx = STEP_ORDER.indexOf(resolved);
  if (idx < 0) return 0;
  return Math.round(((idx + 0.5) / STEP_ORDER.length) * 100);
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function getInitials(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

// Composite key for talentId+domain uniqueness in selection
export function ckey(talentId: string, domain: string): string {
  return `${talentId}_${domain}`;
}
